import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { V4_DIALOG_CONTENT, V4_DIALOG_MD } from '../../theme/v4Compat';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainDeviceResultDeviceFirmwareViewModel } from '../../api/HealthSystemApi';
import { MarkdownContent } from '../../elements';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { DeviceFirmwareType } from '../../models/device';

interface FirmwareReleaseNotesModalProps {
  /** The firmware whose release log is shown; null keeps the dialog closed. */
  firmware: HealthSystemDomainDeviceResultDeviceFirmwareViewModel | null;
  /** Editing calls an admin-only endpoint, so the affordance follows the same role. */
  canEdit?: boolean;
  onClose: () => void;
  /** Hands the firmware to the editing dialog. */
  onEdit?: (firmware: HealthSystemDomainDeviceResultDeviceFirmwareViewModel) => void;
}

const FirmwareReleaseNotesModal: React.FC<FirmwareReleaseNotesModalProps> = ({
  firmware,
  canEdit = false,
  onClose,
  onEdit
}) => {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();

  const releaseNotes = firmware?.releaseNotes?.trim() ?? '';

  return (
    <Dialog
      id="firmwareReleaseNotesDialog"
      open={firmware !== null}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={V4_DIALOG_MD}
    >
      <DialogTitle component="div" className="!border-b !border-gray-200 !px-6 !py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <ArticleOutlinedIcon fontSize="small" />
          </span>

          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900">
              {t('f-device-release-notes-title', { version: firmware?.version ?? '' })}
            </h2>
            <p className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
              {firmware && <span>{DeviceFirmwareType[firmware.deviceFirmwareType]}</span>}
              {firmware?.fileName && <span>&middot; {firmware.fileName}</span>}
              {firmware?.dateTime && <span>&middot; {formatDate(firmware.dateTime)}</span>}
            </p>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5" sx={V4_DIALOG_CONTENT}>
        {firmware?.info && (
          <p className="mb-4 rounded-md border-l-4 border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
            {firmware.info}
          </p>
        )}

        {releaseNotes === '' ? (
          <p className="py-6 text-center text-sm text-gray-400 italic">{t('f-device-release-notes-empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <MarkdownContent>{releaseNotes}</MarkdownContent>
          </div>
        )}
      </DialogContent>

      {/* Close sits left as the way out and Edit right as the action, matching the edit dialog's
          Cancel and Save. */}
      <DialogActions className="!border-t !border-gray-200 !px-6 !py-3">
        <Button onClick={onClose} variant="outlined" color="primary">
          {t('f-com-close')}
        </Button>

        {canEdit && firmware && (
          <Button
            onClick={() => onEdit?.(firmware)}
            variant="contained"
            color="primary"
            startIcon={<EditOutlinedIcon />}
          >
            {t('f-com-edit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FirmwareReleaseNotesModal;
